import * as Debug from 'debug';
import { AfterViewInit, Component, ElementRef, Inject, Input, OnChanges, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges, ViewChild} from '@angular/core';
import { ActivatedRoute, Router} from '@angular/router';
import { NoteService, PurchaseMicroService} from '../services/note_service';
import { StreamService} from '../services/stream_service';
import { TeamService} from './../services/team_service';
import { NotificationsService} from '../services/notifications_service';
import { ModalDismissReasons, NgbModal, NgbModalOptions, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import { ReportNoteModalComponent} from './report-note-modal/report-note-modal.component';
import { UserService} from '../services/user_service';
import { RateNoteModalComponent} from './rate-note-modal/rate-note-modal.component';
import { ConfirmationModalComponent} from '../confirmation-modal/confirmation-modal.component';
import { CacheService} from '../services/cache_service';
import { User} from '../types/user';
import { Util} from '../../shared/util/util';
import { StreamKind} from '../../shared/types/stream_kind';

import { Note} from '../types/note';
import { Stream} from '../types/stream';
import { isPlatformBrowser, Location} from '@angular/common';
import { SocialSharingComponent } from '../social-sharing/social-sharing.component';
import { SubscriptionPlansComponent } from '../subscribe-plans/subscribe-plans.component';
import { GeoService } from '../services/geographic_service';
import { GtmService } from '../services/gtm_service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SubscriptionStatus } from '../types/subscription_status';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FlagReason } from '../types/flag-reason';

const debug = Debug('notd: note-detailed');

@Component({
  selector: 'note-detailed-view',
  templateUrl: './note-detailed.component.html',
  styleUrls: ['./note-detailed.component.scss']
})
export class NoteDetailedComponent implements OnInit, OnDestroy, OnChanges {
  @Input() note: Note;
  @ViewChild(SubscriptionPlansComponent, {static: false}) public subscriptionPlans: SubscriptionPlansComponent;
  @ViewChild(SocialSharingComponent, {static: false}) public socialSharingComp: SocialSharingComponent;
  @ViewChild('noteReportModal', {static: false}) public noteReportModal: ElementRef;
  @ViewChild('premiumContentWrapper', { static: false }) premiumContent!: ElementRef;
  @ViewChild('premiumContentWrapper') set premiumContentWrapper(element) {
    if (element && isPlatformBrowser(this.platformId)) {
      const evt =
        new CustomEvent(
          'paid-content-loaded',
          {
            detail: null,
            bubbles: true
          });

          window.dispatchEvent(evt);
    }
  }

  // just for this single file disable, because many things to override
// https://stackoverflow.com/a/66001071/374421
/* eslint-disable @typescript-eslint/member-ordering */

  public newComment: string;
  public purchaseService: PurchaseMicroService;

  userRate: any = 0;
  rateStarsClicked = false;
  extendCommentSpace = false;
  isLoggedIn = false;
  isNoteOwner = false;
  isBookmarked = false;
  isUserCommunityMember = false;
  showPreviousThreadLink = false;
  showNextThreadLink = false;
  extendComments = true;
  enabledComments = false;
  loadingContent = true;
  loadingLoginPage = false;
  loadingNoteComments = true;
  annotationAllowed = false;
  annotationStatus = false;
  noteStream: Stream;
  myUser: User;
  notifications$;
  subscription$;
  noteTags = [];
  fileAudioIncluded = false;
  communitySub: any;
  teamSubscriptionStatus: any;
  streamSubscriptionStatus: any;
  subscriptionPlansPrice = {};
  public userIsAlreadyACommunityMember = false;
  public streamKind = StreamKind;
  public notUSCitizen = true;
  public userRequiredLoginOrRegister: boolean;
  public isLoggedInRequirePaymentNoteNotFree: boolean;
  public isNotLoggedInRequirePaymentNoteNotFree: boolean;
  public isNotLoggedInNoteIsFree: boolean;
  public isStreamOpenCommunityAndUserIsMemeber: boolean;
  public commentError = {
    show: false,
    message: ''
  };
  public canDeleteNote = false;
  public isUserAdmin = false;
  public isStreamFree = false;
  public isNoteFree = false;
  public noteReportForm: UntypedFormGroup;
  public reportFlagsList: {key: string; value: string; selected: boolean}[];
  public reportNoteFormSubmited = false;
  public noteNeedsPayment = true;

  public alert: {type: string; message: string; timeout?: number, floating?: boolean, gtmClass?: string};

  private reportModalRef: NgbModalRef;
  private hastTag: string;
  private twitterScriptLoaded = false;
  private premiumContentIsLoaded = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private noteService: NoteService,
    private streamService: StreamService,
    private teamService: TeamService,
    private notificationsService: NotificationsService,
    private modalService: NgbModal,
    private userService: UserService,
    private util: Util,
    private cacheService: CacheService,
    private gtmService: GtmService,
    private geoService: GeoService,
    private location: Location,
    private fb: UntypedFormBuilder,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) protected platformId: Object) {
  }

  ngOnInit(): void {
    this.isLoggedIn = this.userService.isLoggedIn();
  }

  initComponentData(): void {
    const snapshotData = this.route.snapshot.data;

    this.isLoggedIn = this.userService.isLoggedIn();

    this.noteService.getNotePurchaseStatus(this.note.id).then(status => {
      this.noteNeedsPayment = this.isUserAdmin || (status.hasOwnProperty('hasAccess') && status['hasAccess']) ? false : true;
      this.isStreamFree = status.channel && status.channel.kind === StreamKind['Free'] ? true : false;
      this.isNoteFree = snapshotData.note.price === 0 ? true : false;
      this.checkUserAccess();

      if (!this.noteNeedsPayment) {
        this.noteService.loadNotePremiumContent(this.note).then((note: Note) => {
          this.note = this.updateNoteStream(note);
          this.loadingContent = false;

          this.scrollToAnchorOnNoteLoad();
          this.gtmService.pushTag({
            event: 'load-paid-content-note',
            noteId: this.note.id.toString(),
          });
        })
        .catch((resp) => {
          console.error('NoteDetailedComponent error retrieving note premium content', resp);
          this.loadingContent = false;
        });
      } else {
        this.noteService.loadNoteLead(this.note).then((note: Note) => {

          if (
            !this.note.leadContent && 
            note.leadContent && 
            note.leadContent['changingThisBreaksApplicationSecurity']) {
              this.note.leadContent = note.leadContent['changingThisBreaksApplicationSecurity'];
          }
          this.note = this.updateNoteStream(note);
          this.loadingContent = false;
          this.scrollToAnchorOnNoteLoad();

          this.gtmService.pushTag({
            event: 'load-unpaid-content-note',
            noteId: this.note.id.toString(),
          });
        });
      }
      
      this.note.status = this.isUserAdmin || (status.hasOwnProperty('hasAccess') && status['hasAccess']) ? 1 : 402;
      this.noteStream = snapshotData.note.stream;
    
      this.myUser = new User(this.note.user);
      this.hastTag = this.route.snapshot.fragment ? this.route.snapshot.fragment : null;

      if (!this.note.rating) {
        this.note.rating = this.note.ratingAvg;
      }

      if (snapshotData.user && snapshotData.user.id) {
        const user = snapshotData.user;
        this.isNoteOwner = (user.id.toString() === this.note.userId.toString()) ? true : false;
        this.canDeleteNote = this.canBeDeleted(snapshotData.note, this.noteStream, user);
        this.isUserAdmin = !!user.admin;
        this.setBookmarkStatus(user);
        this.checkUserAccess();
        this.getCommunityStatus();
      }

      if (!this.note.stream && this.note.channelId) {
        this.streamService.getStreamInfoObservable(this.note.channelId)
          .subscribe((stream: Stream) => {
            if (stream) {
              Object.assign(this.note, {
                stream
              });
            }
          }, err => {
            console.log(`channel ${this.note.channelId} does not exist`);
          });
      }
    }, err => {
      console.error('There was an error while getting the note purchase status', err);
      this.loadingContent = false;
      this.checkUserAccess();
    })
    .catch((resp) => {
      console.error('NoteDetailedComponent error retrieving note premium content', resp);
      this.loadingContent = false;
      this.checkUserAccess();
    });

    if (this.isLoggedIn && this.route.snapshot.queryParams['t']) {
      const urlToken = this.route.snapshot.queryParams['t'];
      const jwtHelper: JwtHelperService = new JwtHelperService();

      try {
        if (jwtHelper.decodeToken(urlToken)) {
          const decodedToken = jwtHelper.decodeToken(urlToken);

          this[decodedToken.meta.channelId ? 'streamService' : 'teamService']
            .validateSubscription(
              this.route.snapshot.queryParams['t'],
              decodedToken.meta.channelId ? decodedToken.meta.channelId : decodedToken.meta.teamId).then(validate => {
            this.loadingContent = true;
            if (validate.status === SubscriptionStatus.Active ) {
              this.removeUrlParam();
              this.noteService.loadNotePremiumContent(this.note).then(note => {
                this.note.needsPayment = function() {
                  return false;
                };
                this.noteNeedsPayment = false;
                this.note = this.updateNoteStream(note);
                this.checkNotificationList();
                this.scrollToAnchorOnNoteLoad();
                this.checkUserAccess();
                this.getMorePaidNoteDetails();

                this.loadingContent = false;

                this.gtmService.pushTag({
                  event: 'load-paid-content-note-after-subscription',
                  noteId: this.note.id.toString(),
                });

                this.setAlertMessage({
                  type: 'success',
                  message: `Your subscription has been activated.`,
                  timeout: 10000,
                  floating: false,
                  gtmClass: 'gtm_subsciption_thankyou'
                });
              })
              .catch((resp) => {
                console.log('error retrieving note premium content', resp);
                this.loadingContent = false;
              });
            } else {
              this.loadingContent = false;
              this.errorMessage(
                'Error Confirming Payment.',
                // eslint-disable-next-line max-len
                'We encountered an issue while confirming your payment. Please try again or contact our customer service for assistance.');
            }
          }, err => {
            console.error('Error while subscription validate', err.error || err);
            this.loadingContent = false;
            this.checkUserAccess();
          })
          .catch(err => {
            console.error('Error while subscription validate', err.error || err);
            this.loadingContent = false;
            this.checkUserAccess();
          });
        }
      } catch (err) {
        console.error('Looks like url doesn`t have proper token or it is a part of application url param.', err);
        // this.errorMessage(
        //   'Error Confirming Payment.',
        //   'We encountered an issue while confirming your payment. Please try again or contact our customer service for assistance.');
      }
    }

    this.streamSubscriptionStatus = this.streamService.getSubscriptionStatusUpdate().subscribe(res => {
      console.log('getSubscriptionStatusUpdate', res)
      if (res.active === 1 || res.price === 0) {
        Object.assign(this.note, {
          status: res.active
        });

        this.noteNeedsPayment = false;

        this.noteService.loadNotePremiumContent(this.note)
          .then((note) => {
            this.note = this.updateNoteStream(note);
            this.checkNotificationList();
            this.scrollToAnchorOnNoteLoad();
            this.checkUserAccess();
            this.getMorePaidNoteDetails();

            this.gtmService.pushTag({
              event: 'load-paid-content-note-after-subscription',
              noteId: this.note.id.toString(),
            });
          })
          .catch((resp) => {
            console.log('error retrieving note premium content', resp);
          });
      }
    });

    this.teamSubscriptionStatus = this.teamService.subscriptionStatusUpdateSubject.subscribe(res => {
      console.log('teamService subscriptionStatusUpdateSubject', res);
        Object.assign(this.note, {
          status: res.active
        });

        this.noteNeedsPayment = false;

        this.noteService.loadNotePremiumContent(this.note)
          .then((note) => {
            this.note = this.updateNoteStream(note);
            this.checkNotificationList();
            this.scrollToAnchorOnNoteLoad();
            this.checkUserAccess();
            this.getMorePaidNoteDetails();

            this.gtmService.pushTag({
              event: 'load-paid-content-note-after-team-subscription',
              noteId: this.note.id.toString(),
            });
          })
          .catch((resp) => {
            console.log('error retrieving note premium content', resp);
          });
    });

    this.checkNotificationList();
    this.checkForParentChildNotes();
    this.getCommentsStatus();
    this.getNoteUser();
    this.setNoteTags();
    this.getNoteComments();
    this.getSubscriptionPlans();
    this.createReportNoteForm();

    if (this.isLoggedIn && (this.isUserAdmin || this.isNoteFree || this.isNoteOwner || !this.noteNeedsPayment)) {
      this.getMorePaidNoteDetails();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.note.currentValue) {
      this.initComponentData();
    }
  }

  getCommentsStatus() {
    this.enabledComments = (this.note.enComments === 1) ? true : false;
  }

  getNoteComments() {
    if (this.enabledComments) {
      this.enabledComments = true;
        this.noteService.getNoteComments(this.note.id).then(res => {
            Object.assign(this.note, {
                comments: res
            });
            this.loadingNoteComments = false;
        });
    }
  }

  public addNewComment() {
    if (this.newComment && this.newComment !== '') {
      this.noteService.commentOnNote(this.note.id, this.removeNewlineFromComment(this.newComment)).then((comment) => {
        this.newComment = '';
        this.refreshComments();
      }, err => {
        console.error('adding commentes error', err);
        const codeError = err.error.code || '';
        let message: string;

        switch(codeError) {
          case 'COMMENTING_BLOCKED':
            message = 'Your account has been blocked from commenting on this stream.';
          break;
          default:
            message = 'There was and error while adding your comment, please try again.';
          break;
        }

        this.commentError = {
          show: true,
          message
        };
      });
    }
  }

  public openRateNoteModal(): void {
    const modalRef = this.modalService.open(RateNoteModalComponent, { windowClass: 'rate-note-modal', size: 'lg' });


    modalRef.result.then(() => {
      this.noteService.loadNotePremiumContent(this.note)
          .then((note) => {
            this.note = this.updateNoteStream(note);
          })
          .catch((resp) => {
            console.log('error retrieving note premium content', resp);
          });
    }, (reason) => {
      console.log(`Dismissed ${this.getDismissReason(reason)}`);
      this.rateStarsClicked = false;
    });

    modalRef.componentInstance.note = this.note;
    modalRef.componentInstance.userRate = this.userRate;
    modalRef.componentInstance.previousRatings = this.note.ratings;
  }

  public onRateChange(event: Event): void {
    if (this.rateStarsClicked) {
      this.userRate = event;
      this.openRateNoteModal();
    }
  }

  handleRateChange() {
    this.rateStarsClicked = true;
  }

  public openSubscribePlansModal(): void {
    this.subscriptionPlans.showSubscriptionsModal();
  }

  loggedInUserIsAuthor() {
    return this.userService.curUser && this.userService.curUser.id === this.note.user.id;
  }

  purchaseNote() {
    if (!this.isLoggedIn) {
      return;
    }

    this.purchaseService.purchase().then((note) => {
      this.note.premiumContent = note.premiumContent;
      this.note.premiumMedia = note.premiumMedia;
      this.note.premMedia = note.premMedia;
      this.note.status = 200;
    }).catch((err) => {
      console.error(err);
      // FIXME (karl) // don't alert but show something usefull to user
      alert('Failed ' + err.toString());
    });
  }

  deleteNote() {
    const modalOptions: NgbModalOptions = {};
    const refModalConf = this.modalService.open(ConfirmationModalComponent, modalOptions);

    refModalConf.componentInstance.confirmationModalTitle = 'Delete note';
    refModalConf.componentInstance.confirmationModalMsg = 'Are you sure you want to delete this note?';

    refModalConf.result.then(resp => {
      if (resp) {
        this.noteService.deleteNote(this.note.id).subscribe(() => {
          this.router.navigate(['/streams', this.note.channelId, 'view', this.note.stream.indexedName || this.note.stream.name]);
        });
      }
    });
  }

  toggleBookmark() {
    if (!this.isBookmarked) {
      this.noteService.bookmarkNote(this.note.id).then(() => {
        this.isBookmarked = true;
      });
    } else {
      this.noteService.removeBookmark(this.note.id).then(() => {
        this.isBookmarked = false;
      });
    }
  }

  extendCommentArea() {
    this.extendCommentSpace = (this.newComment && this.newComment.length > 200) ? true : false;
  }

  refreshComments() {
    this.noteService.getNoteComments(this.note.id).then(comments => {
      this.note.comments = comments;
    });
  }

  setBookmarkStatus(user: User) {
    if (user.savedPosts) {
      for (const postId of Object.keys(user.savedPosts)) {
        if (postId === this.note.id) {
          this.isBookmarked = true;
        }
      }
    }
  }

  checkNotificationList() {
    if (this.isLoggedIn) {
      if (this.notifications$) {
          this.notifications$.unsubscribe();
      }

      this.notifications$ = this.notificationsService.notificationList$.subscribe(nList => {
        if (!nList || nList.length === 0) {
          return;
        }
        const promisesArray: any = [];
        if (nList.length > 0) {
          nList.map((notify: any) => {
            if (notify.postId === this.note.id) {
              promisesArray.push( this.notificationsService.deleteNotification(notify.id) );
            }
          });
        }

        if (promisesArray.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          Promise.all(promisesArray).then(() => { })
          .catch(() => {
            console.log('Error while removing notification');
          });
        }
      });
    }
  }

  toggleComments() {
    if (!this.loadingNoteComments) {
      this.extendComments = !this.extendComments;
    }
  }

  checkForParentChildNotes() {
    if (this.note.parentPostId.length > 0) {
      this.showPreviousThreadLink = true;
      // this.router.navigate(['/thread', this.note.parentPostId]);
    }

    if (this.note.childPosts.length > 0) {
      this.showNextThreadLink = true;
      // this.router.navigate(['/thread', this.note.id, { cn: this.note.childPosts } ]);
    }
  }

  showNotes(type: string) {
    if (type === 'parent') {
      this.router.navigate(['/thread', this.note.parentPostId]);
    } else {
      this.router.navigate(['/thread', this.note.childPosts[0]]);
    }
  }

  annotationAllowCheck() {
    if (this.isLoggedIn) {
      this.noteService.canAnnotate(this.note.id).then(res => {
        if (((res && res.annotationOK) || this.isUserAdmin) && !this.isIosDevice()) {
          this.annotationAllowed = true;
        }
      }, e => {
        console.error('Annotation error: ', e);
      }).catch(e => {
        console.error('Annotation error', e);
      });
    }
  }

  annotationStatusChange(event: any) {
    this.annotationStatus = event;
  }

  checkUserCommunityMember() {
    if (this.isLoggedIn) {
      this.streamService.getCommunityMembersList(this.note.channelId)
        .toPromise()
        .then(res => {
            this.isUserCommunityMember = res.filter(member => member.userId === this.route.snapshot.data.user.id).length > 0;
        }, err => {
          console.error('Error fetching community members of channel ID', this.note.channelId, err.error || '');
        })
        .catch(err => {
          console.error('Error fetching community members of channel ID', this.note.channelId, err.error || '');
        });
    }
  }

  getNoteUser() {
    if (!this.note.user) {
      this.userService.getUserProfile(this.note.userId)
          .then(res => {
            this.myUser = new User(res);
            Object.assign(this.note, {
              user: res
            });
          }, err => {
            console.error('There was and issue while fetching user profile', err);
          });
    }
  }

  getMetaNoteDescription() {
      return this.note.leadContent || this.note.firstPremWords;
  }

  errorMessage(title: string, message: string) {
    const modalOptions: NgbModalOptions = {};
    const refModalConf = this.modalService.open(ConfirmationModalComponent, modalOptions);

    refModalConf.componentInstance.onlyMessage = true;
    refModalConf.componentInstance.confirmationModalTitle = title;
    refModalConf.componentInstance.confirmationModalMsg = message;

    refModalConf.result.then(resp => {
      if (resp) {
        console.log(resp);
      }
    });
  }

  public joinOpenCommunity() {
    this.streamService.joinOpenCommunity(this.note.channelId).then(
      () => {
        this.showConfirmationModal('Welcome', 'You have joined ' + this.note.stream.name);
        this.streamService.joinedCommunitySub.next(true);
        this.userIsAlreadyACommunityMember = true;
        this.isStreamOpenCommunityAndUserIsMemeber =
          this.note.stream && (this.note.stream.kind === this.streamKind.OpenCommunity && this.userIsAlreadyACommunityMember);
      },
      err => {
        if (err.code) {
          switch (err.code) {
            case 'ALREADY_EXISTS':
              this.showConfirmationModal('Error', 'You have already joined ' + this.note.stream.name);
              break;
            default:
          }
        }
      }
    );
  }

  getUrlifyName() {
    return this.noteService.urlifyName(this.note.name);
  }

  checkForMediaFiles() {
    if (!isPlatformBrowser(this.platformId)) {
      // not supported on server side because interval will hang server
      return;
    }
    if (this.note.premMedia && this.note.premMedia.filter(m => m.mime && m.mime.indexOf('audio') >= 0).length > 0) {
      this.listenMediaFileStatus(this.note.id);
      this.fileAudioIncluded = true;
    } else {
      setTimeout(() => {
        this.noteService.getNoteInfo(this.note.id, true).then((note: Note) => {
          if (note.premMedia && note.premMedia.filter(m => m.mime && m.mime.indexOf('audio') >= 0).length > 0) {
            this.listenMediaFileStatus(this.note.id);
            this.fileAudioIncluded = true;
          }
        });
      }, 3000);
    }
  }

  listenMediaFileStatus(noteId: string) {
    if (!isPlatformBrowser(this.platformId)) {
      // not supported on server side because interval will hang server
      return;
    }

    const videoInterval = setInterval(() => {
      this.noteService.getNoteInfo(noteId, true).then((res: Note) => {
        const mediaFile = res.premMedia.filter(m => m.mime.indexOf('audio') >= 0)[0];
        if (mediaFile && mediaFile.transcoded && mediaFile.transcoded.state === 'COMPLETED') {
          clearInterval(videoInterval);
        }
        if (mediaFile && mediaFile.transcoded && mediaFile.transcoded.state === 'ERROR') {
          clearInterval(videoInterval);
        }
      });
    }, 4000);
  }

  public closeErrorMessage(): void {
    this.commentError = {
      show: false,
      message: ''
    };
  }

  ngOnDestroy() {
    if (this.notifications$) {
        this.notifications$.unsubscribe();
    }

    if (this.subscription$) {
        this.subscription$.unsubscribe();
    }

    if (this.teamSubscriptionStatus) {
      this.teamSubscriptionStatus.unsubscribe();
    }

    if (this.streamSubscriptionStatus) {
      this.streamSubscriptionStatus.unsubscribe();
    }

    if (this.communitySub) {
      this.communitySub.unsubscribe();
    }
  }

  public openSocialModal() {
    this.socialSharingComp.openModal();
  }

  public openNoteReportModal(): void {
    this.reportModalRef = this.modalService.open(this.noteReportModal, {
      windowClass: 'note-report-modal',
      size: 'lg'
    });

    this.reportModalRef.result.then(() => {
      this.resetReportForm();
    }, () => {
      this.resetReportForm();
    });
  }

  public selectNoteReportType(sr: any): void {
    for (const r of this.reportFlagsList) {
      if (r.key === sr.key) {
        r.selected = true;
        this.noteReportForm.get('reason').patchValue(r);
      } else {
        r.selected = false;
      }
    }
  }

  public sendNoteReport(): void {
    this.reportNoteFormSubmited = true;

    if (this.noteReportForm.valid) {
      this.reportModalRef.close();
      this.noteService.flagPost(
        this.note.id,
        this.noteReportForm.get('reason').value ? this.noteReportForm.get('reason').value.key : '',
        this.noteReportForm.get('comment').value)
        .catch(err => {
          console.error('There was and error while sending note report', err);
        });
    }
  }

  public premiumContentLoaded(): void {
    if (!this.premiumContentIsLoaded) {
      console.log('premiumContentLoaded');
      this.convertParagraphsToEmbeds();
      this.premiumContentIsLoaded = true;
    }
  }

  private resetReportForm(): void {
    this.noteReportForm.reset();
    this.reportNoteFormSubmited = false;
    for (const r of this.reportFlagsList) {
      r.selected = false;
    }
  }

  private canBeDeleted(note: Note, stream: Stream, user: User): boolean {
    if (user['admin']) {
      return true;
    }

    if (note.userId.toString() === user.id.toString()) {
      return true;
    }

    if (stream.userId.toString() === user.id.toString()) {
      return true;
    }

    if (stream.admins && stream.admins.indexOf(parseInt(user.id, 10)) !== -1) {
      return true;
    }

    return false;
  }

  private checkUserAccess(): void {
    this.userRequiredLoginOrRegister =
      !this.isLoggedIn ||
      (this.isLoggedIn && this.noteNeedsPayment && this.note.price !== 0) ||
      (!this.isLoggedIn && this.noteNeedsPayment && this.note.price !== 0) ||
      (!this.isLoggedIn && this.note.price === 0);

      this.isLoggedInRequirePaymentNoteNotFree = this.isLoggedIn && this.noteNeedsPayment && this.note.price !== 0;
      this.isNotLoggedInRequirePaymentNoteNotFree = !this.isLoggedIn && this.noteNeedsPayment&& this.note.price !== 0;
      this.isNotLoggedInNoteIsFree = !this.isLoggedIn && this.note.price === 0;
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return  `with: ${reason}`;
    }
  }

  private checkLogin(): boolean {
    if (!this.userService.isLoggedIn()) {
      this.userService.login().then(() => {
        this.cacheService.clearCache();
      });
      return false;
    } else {
      return true;
    }
  }

  private showConfirmationModal(title: string, message: string) {
    const modalOptions: NgbModalOptions = {};
    const refModalConf = this.modalService.open(ConfirmationModalComponent, modalOptions);

    refModalConf.componentInstance.onlyMessage = true;
    refModalConf.componentInstance.confirmationModalTitle = title;
    refModalConf.componentInstance.confirmationModalMsg = message;
  }

  private isIosDevice(): boolean {
    const userAgent = (window as any).navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test( userAgent );
  }

  private updateNoteStream(note: Note): Note {
    const noteStream = !note.stream ? (this.route.snapshot.data.note.stream) : note.stream;
    return Object.assign(note, {
      stream: noteStream || ''
    });
  }

  private scrollTo(target: any): void {
    const config: any = {
        target,
        duration: 500,
        easing: 'easeOutQuad',
        offset: 20
    };
  }

  private scrollToAnchorOnNoteLoad(): void {
    if (this.hastTag) {
      setTimeout(() => {
        this.scrollTo(this.hastTag);
      }, 0);
    }
  }

  private convertParagraphsToEmbeds(): void {
    const premiumContentNode = this.premiumContent?.nativeElement;
    if (!premiumContentNode) return;

    let hasTwitterEmbed = false;
  
    premiumContentNode.querySelectorAll('p[data-embed-type]').forEach(paragraph => {
      const embedType = paragraph.getAttribute('data-embed-type');
      const embedUrl = paragraph.getAttribute('data-embed-url') || '';
      const embedId = paragraph.getAttribute('data-embed-id') || '';
  
      if (embedType === 'custom' && embedUrl) {
        this.getOpengraphPreview(embedUrl).then(data => {
          this.createOpenGraphEmbed(data).then(container => {
            paragraph.replaceWith(container);
          })
        },
        () => {
          console.error('There was an error while fetching opengraph preview for', encodeURIComponent(embedUrl));
        });
      }

      if (embedType === 'youtube' && embedId) {
        const youtubeEmbed = this.createYoutubeEmbed(embedId);
        paragraph.replaceWith(youtubeEmbed);
      }

      if (embedType === 'twitter' && embedId) {
        const twitterEmbed = this.createTwitterEmbed(embedId);
        paragraph.replaceWith(twitterEmbed);
        hasTwitterEmbed = true;
      }

      if (hasTwitterEmbed && !this.twitterScriptLoaded) {
        this.loadTwitterScript();
      }
    });
  }

  private getOpengraphPreview(embedUrl: string): Promise<Record<string, unknown> | null> {
    return fetch(`/api/services/opengraph/preview?url=${encodeURIComponent(embedUrl)}`)
      .then((response) => {
        if (!response.ok) {
          console.error('Error while fetching embed data:', response.status, response.statusText);
          return null;
        }
        return response.json();
      })
      .then(data => {
        if (data && data.parsed_opengraph && data.parsed_opengraph.image && data.parsed_opengraph.title) {
          const { favicon, ogUrl, requestUrl } = data.raw_opengraph || {};
          const favFullUrl = this.isValidUrl(favicon)
            ? favicon
            : this.isValidUrl(ogUrl || requestUrl) && favicon
              ? this.getMainDomain(true, ogUrl || requestUrl) + favicon
              : false;

          const image = data.parsed_opengraph.image;
          const imagePromises: Promise<{ type: 'favicon' | 'thumbnail'; data: string | null }>[] = [];

          let updatedImgFavicon: string | null = null;
          let updatedImgThumb: string | null = null;

          const noteId = this.note.id;

          if (favFullUrl && !this.containsAuthParam(favFullUrl)) {
            imagePromises.push(
              favFullUrl ? this.addMediaToServer(noteId, favFullUrl).then(updatedImage => ({ type: 'favicon', data: updatedImage })) : Promise.resolve({ type: 'favicon', data: null })
            );
          } else if (favFullUrl) {
            updatedImgFavicon = null;
          }

          if (image && !this.containsAuthParam(image)) {
            imagePromises.push(
              image ? this.addMediaToServer(noteId, image).then(updatedImage => ({ type: 'thumbnail', data: updatedImage })) : Promise.resolve({ type: 'thumbnail', data: null })
            );
          } else if (image) {
            updatedImgThumb = null;
          }

          return Promise.all(imagePromises).then(results => {
            results.forEach(result => {
              console.log(`Type: ${result.type}, Data: ${result.data ? result.data.substring(0, 50) + '...' : null}`);
              if (result.type === 'favicon' && result.data) {
                updatedImgFavicon = result.data;
              } else if (result.type === 'thumbnail' && result.data) {
                updatedImgThumb = result.data;
              }
            });

            return {
              raw_opengraph: {
                ...data.raw_opengraph,
                updatedImgFavicon,
                updatedImgThumb,
              },
              parsed_opengraph: data.parsed_opengraph,
            };
          });
        }

        return null;
      })
      .catch(error => {
        console.error('Error while fetching embed data', error);
        return null;
      });
  }

  addMediaToServer(noteId: string | number, url: string): Promise<string | null> {
    return fetch(`/api/post/${noteId}/premium/media/add`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url }),
      })
      .then(response => {
        if (!response.ok) {
          console.error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        return data.download ? data.download : null;
      })
      .catch(error => console.error('Error sending data to server:', error));
  }

  private createOpenGraphEmbed(data): Promise<HTMLElement> {
    const { title, description, image, site_name } = data.parsed_opengraph || {};
    const { favicon, ogUrl, requestUrl, ogSiteName, updatedImgFavicon, updatedImgThumb } = data.raw_opengraph || {};
    const domain = this.getMainDomain(false, ogUrl || requestUrl || '');
    const favFullUrl = this.isValidUrl(favicon)
      ? favicon
      : this.isValidUrl(ogUrl || requestUrl) && favicon
      ? this.getMainDomain(true, ogUrl || requestUrl) + favicon
      : '';

    return Promise.resolve(
      (() => {
        const container = document.createElement('div');
        container.classList.add('opengraph-embed');
        container.setAttribute('data-embed-url', ogUrl || requestUrl || '');

        container.innerHTML = `
          <a href="${ogUrl || requestUrl || '#'}" target="_blank" contenteditable="false" rel="noopener noreferrer" class="opengraph-wrapper">
            <div class="opengraph-content">
              <h2 class="opengraph-logo-name">
                ${
                  favFullUrl && updatedImgFavicon
                    ? `<img src="${updatedImgFavicon}" contenteditable="false" class="opengraph-favicon" alt="Favicon">`
                    : ''
                }
                <p class="opengraph-name">${site_name || ogSiteName || domain}</p>
              </h2>
              ${
                image && updatedImgThumb
                  ? `<div class="opengraph-image-wrapper"><img contenteditable="false" src="${updatedImgThumb}" alt="${title || 'Preview'}" class="opengraph-image"></div>`
                  : ''
              }
              <div class="opengraph-text">
                ${title ? `<h2 class="opengraph-title">${title}</h2>` : ''}
                ${description ? `<p class="opengraph-description">${description}</p>` : ''}
              </div>
            </div>
          </a>
        `.replace(/\n\s*/g, '');

        const imageWrapper = container.querySelector('.opengraph-image-wrapper');
        const imageEl = imageWrapper?.querySelector('.opengraph-image');

        if (
          imageWrapper &&
          imageEl &&
          this.hasMatchingDomain(ogUrl || requestUrl || '')
        ) {
          imageEl.addEventListener('load', () => {
            imageWrapper.classList.add('video-play-icon');
          });
        }

        return container;
      })()
    );
}


  private createYoutubeEmbed(videoId: string): HTMLElement {
    const container = document.createElement('div');
    container.classList.add('yt-embed');
    container.setAttribute('data-video-id', videoId);
  
    container.innerHTML = `
      <iframe 
        width="560" height="315"
        src="https://www.youtube.com/embed/${videoId}" 
        frameborder="0" allowfullscreen>
      </iframe>`;
  
    return container;
  }

  private createTwitterEmbed(tweetId: string): HTMLElement {
    const container = document.createElement('div');
    container.classList.add('tweet-container');
    container.setAttribute('data-tweet-id', tweetId);
  
    return container;
  }

  private loadTwitterScript(): void {
    if (this.twitterScriptLoaded) return;
  
    this.loadScript('//platform.twitter.com/widgets.js').then(() => {
      this.twitterScriptLoaded = true;
  
      const tweetContainers = document.querySelectorAll('.tweet-container');
  
      tweetContainers.forEach(container => {
        const tweetId = container.getAttribute('data-tweet-id');
  
        if (!container.hasAttribute('data-embedded')) {
          window['twttr'].widgets.createTweet(tweetId, container);
          container.setAttribute('data-embedded', 'true');
          container.classList.add('ql-align-center');
        }
      });
    }).catch(error => {
      console.error('Twitter script failed to load:', error);
    });
  }
  
  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Script load error for ${src}`));
      document.head.appendChild(script);
    });
  }  

  private isValidUrl(url: string): boolean {
    try {
      return Boolean(new URL(url));
    } catch {
      return false;
    }
  }
  
  private containsAuthParam(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.searchParams.has('auth');
    } catch (error) {
      console.error('Error parsing URL:', error);
      return false;
    }
  }

  private getMainDomain(fullDomain: boolean, url: any) {
    try {
      const parsedUrl = new URL(url);
      return fullDomain ? `${parsedUrl.protocol}//${parsedUrl.hostname}` : parsedUrl.hostname.replace(/^www\./, '');
    } catch (error) {
      console.error('Invalid URL:', url);
      return url;
    }
  }

  private hasMatchingDomain(fullUrl: string): boolean {
    const addPlayIconDomains = ['postd.io'];

    try {
      for (const domain of addPlayIconDomains) {
        if (fullUrl.includes(domain)) {
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Invalid URL:', fullUrl);
      return false;
    }
  }

  private getSubscriptionPlans(): void {
    Promise.all([
      this.streamService.getStreamSubscriptionPlans(this.note.channelId),
      this.geoService.getCurrentLocation(false)
    ]).then(([plans, location]) => {
      const loc: any = location;
      this.notUSCitizen = loc && loc.country && loc.country !== 'US';

      const plansMinMaxPrices = this.getMinMaxSubscriptionPrice(plans, 'price');
      this.subscriptionPlansPrice = {
        min: plansMinMaxPrices['min'],
        max: plansMinMaxPrices['max']
      };

      // this.isStreamFree =
      //   (plansMinMaxPrices['min'] === 0) ||
      //   (this.route.snapshot.data.requestedStream && this.route.snapshot.data.requestedStream.kind === StreamKind['Free']);
    });
  }

  private getMinMaxSubscriptionPrice(dataObject: any, prop: string) {
    return dataObject.reduce((result, obj) => {
      if (obj[prop] < result.min) {
        result.min = obj[prop];
      }
      if (obj[prop] > result.max) {
        result.max = obj[prop];
      }
      return result;
    }, {min: Number.MAX_VALUE, max: Number.MIN_VALUE});
  }

  private setNoteTags() {
    if (this.note.tags) {
      this.note.tags.map(tag => {
        const newTag = tag.includes('form-') ? tag.replace('form-', 'type-') : tag;
        const splitTag: any[] = newTag.split('-');

        if (splitTag[0] === 'type' || splitTag[0] === 'category') {
          const tagObj = {
            label: (splitTag.length > 2) ? `${splitTag[1].replace('-', '')} ${splitTag[2]}` : splitTag[1].replace('-', ''),
            code: newTag
          };

          this.noteTags.push(tagObj);
        }
      });
    }
  }

  private getCommunityStatus(): void {
    this.communitySub = this.userService.listCommunities().subscribe(comList => {
      if (comList.length > 0) {
          const resp = comList.filter(s => s.id === this.note.channelId);
          this.userIsAlreadyACommunityMember = resp.length > 0;

          this.isStreamOpenCommunityAndUserIsMemeber =
            this.note.stream && (this.note.stream.kind === this.streamKind.OpenCommunity && this.userIsAlreadyACommunityMember);
      }
      }, err => {
        console.log('Error while getting community list', err);
      }
    );
  }

  private removeUrlParam(): void {
    const newUrl = window.location.pathname;
    this.location.replaceState(newUrl);
  }

  private createReportNoteForm(): void {
    this.reportFlagsList = Object.keys(FlagReason)
      .filter(key => isNaN(Number(FlagReason[key])) && key !== '0') 
      .map(key => ({ key, value: FlagReason[key], selected: false }));

    this.noteReportForm = this.fb.group({
      reason: ['', Validators.required],
      comment: ['']
    });
  }

  private getMorePaidNoteDetails(): void {
    this.annotationAllowCheck();
    this.checkUserCommunityMember();
    this.checkForMediaFiles();
  }

  private removeNewlineFromComment(text: string): string {
    return `"${text}"`
      .replace(/'/g, '`')
      .replace(/^"|"$/g, '')
      .replace(/<br\s*\/?>/gi, '\n');
  }

  private convertToBase64(imgSrc: string): Promise<any> {
    const uri = encodeURIComponent(imgSrc);
    return fetch(`/api/util/fetch/data?url=${uri}`)
      .then(response => response.blob())
      .then(blob => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      }))
      .catch(error => console.error('Error fetching data:', error));
  }

  private setAlertMessage(options: {type: string, message: string, timeout?: number, floating?: boolean, gtmClass?: string}): void {
    this.alert = {
      type: options.type,
      message: options.message,
      timeout: options.timeout ?? 5000,
      floating: options.floating ?? false,
      gtmClass: options.gtmClass ?? ''
    };
  }
}
